import { PaymentSummaryCard, PaymentSummaryCardSkeleton, ProfileNotificationHeader } from '@/components/molecules';
import PaymentDetailsDialog from '@/components/organisms/PaymentDetailsDialog';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { useCatalogDynatraceView } from '@/core/hooks/useCatalogDynatraceView';
import { useTranslation, useUIColors } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { useAppStore } from '@/core/stores/appStore';
import {
    getAllOverdueInstallments,
    getAllUpcomingDueInstallments,
    getMostRecentPaidInstallment,
    getPropertiesWithCheques,
    getTotalPropertyPaymentCounts,
    getTotalServiceChargeCounts,
    getTotalServiceChargeOutstanding,
    selectOnlinePaymentIds,
} from '@/modules/payments/utils/installmentUtils';
import { openServiceChargePayments } from '@/modules/payments/utils/openServiceChargePayments';
import { router } from 'expo-router';
import React, { useMemo, useState } from 'react';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export default function PaymentsScreen() {
    useCatalogDynatraceView(DT_SCREEN.PAYMENTS, 'payments_tab_viewed', true);
    const colors = useUIColors();
    const insets = useSafeAreaInsets();
    const { t } = useTranslation();
    const [paymentDetailsVisible, setPaymentDetailsVisible] = useState(false);
    const [selectedPaymentId, setSelectedPaymentId] = useState<string | undefined>(undefined);

    // Get real data from store
    const properties = useAppStore((state) => state.properties);
    const isAdhaJourney = useAppStore((state) => state.isAdhaJourney);
    const unitInstallments = useAppStore((state) => state.unitInstallments);
    const profileLoading = useAppStore((state) => state.profileLoading);
    const unitInstallmentsLoading = useAppStore((state) => state.unitInstallmentsLoading);

    // Calculate payment summary data from real installments
    const paymentSummaryData = useMemo(() => {
        // Property payments (installment type only)
        const propertyPaymentTotals = getTotalPropertyPaymentCounts(unitInstallments, properties);
        const chequesData = getPropertiesWithCheques(unitInstallments, properties);
        const mostRecentPropertyPayment = getMostRecentPaidInstallment(unitInstallments, properties, 'installment');

        // Service charge payments (other_charge type only)
        const serviceChargeTotals = getTotalServiceChargeCounts(unitInstallments, properties);
        const serviceChargeOutstanding = getTotalServiceChargeOutstanding(unitInstallments, properties);
        const mostRecentServiceCharge = getMostRecentPaidInstallment(unitInstallments, properties, 'other_charge');
        
        // Get all upcoming/overdue service charge payments for navigation
        const overdueServiceCharges = getAllOverdueInstallments(unitInstallments, properties).filter(
            item => item.payment_type === 'other_charge'
        );
        const upcomingServiceCharges = getAllUpcomingDueInstallments(unitInstallments, properties).filter(
            item => item.payment_type === 'other_charge'
        );
        
        // Collect all service charge payment IDs (overdue + upcoming due)
        const allServiceChargePaymentIds = [
            ...overdueServiceCharges.map(item => item.id),
            ...upcomingServiceCharges.map(item => item.id)
        ];
        const payableServiceChargePaymentIds = selectOnlinePaymentIds([
            ...overdueServiceCharges,
            ...upcomingServiceCharges,
        ]);

        // Property payments card
        const propertyPayments = {
            id: 'property-payments',
            title: t('payments.propertyPayments'),
            subtitle: `${properties.length} ${properties.length === 1 ? t('payments.property') : t('payments.properties')}`,
            missedCount: propertyPaymentTotals.overdue,
            dueCount: propertyPaymentTotals.upcomingDue,
            totalOutstanding: undefined as number | undefined, // Property payments don't show total outstanding in this card
            cheques: chequesData.map((cheque) => ({
                id: cheque.propertyId,
                propertyName: cheque.propertyName,
                chequeCount: `${cheque.chequeCount} ${cheque.chequeCount === 1 ? t('payments.cheque') : t('payments.cheques')}`,
                amount: cheque.totalAmount,
            })),
            lastPayment: {
                id: 'last-payment',
                propertyName: mostRecentPropertyPayment?.propertyName || properties[0]?.unitNumber || properties[0]?.projectName || 'N/A',
                paymentDate: mostRecentPropertyPayment
                    ? new Date(mostRecentPropertyPayment.paymentDate).toLocaleDateString('en-GB')
                    : new Date().toLocaleDateString('en-GB'),
                paymentDescription: t('payments.lastPayment'),
                amount: mostRecentPropertyPayment?.paid_amount || mostRecentPropertyPayment?.amount || 0,
            },
        };

        // Service charge payments card
        const serviceChargePayments = {
            id: 'service-charge-payments',
            title: t('payments.serviceChargePayments'),
            subtitle: `${properties.length} ${properties.length === 1 ? t('payments.property') : t('payments.properties')}`,
            missedCount: serviceChargeTotals.overdue,
            dueCount: serviceChargeTotals.upcomingDue,
            totalOutstanding: serviceChargeOutstanding,
            cheques: [],
            paymentIds: allServiceChargePaymentIds,
            payablePaymentIds: payableServiceChargePaymentIds,
            lastPayment: {
                id: 'service-charge-last-payment',
                propertyName: mostRecentServiceCharge?.propertyName || properties[0]?.unitNumber || properties[0]?.projectName || 'N/A',
                paymentDate: mostRecentServiceCharge
                    ? new Date(mostRecentServiceCharge.paymentDate).toLocaleDateString('en-GB')
                    : new Date().toLocaleDateString('en-GB'),
                paymentDescription: t('payments.serviceChargePayments'),
                amount: mostRecentServiceCharge?.paid_amount || mostRecentServiceCharge?.amount || 0,
            },
        };

        return [propertyPayments, serviceChargePayments];
    }, [unitInstallments, properties, t]);

    return (
        <View style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}>
            <ScrollView
                contentContainerStyle={styles.scrollContent}
                showsVerticalScrollIndicator={false}
                contentInsetAdjustmentBehavior="never"
            >
                {/* Header - positioned at top, scrolls with content */}
                <View style={styles.headerContainer}>
                    <ProfileNotificationHeader
                        title={t('payments.title')}
                        onProfilePress={() => router.push('/profile')}
                        onNotificationPress={() => router.push('/notifications')}
                        style={{ paddingTop: insets.top + 8 }}
                    />
                </View>

                <View style={styles.content}>
                    {/* Payment Summary Cards - Show skeletons when loading */}
                    <View style={styles.paymentCardsContainer}>
                        {(profileLoading || unitInstallmentsLoading) && paymentSummaryData.length === 0 ? (
                            // Show skeletons when loading
                            <>
                                <PaymentSummaryCardSkeleton />
                                <PaymentSummaryCardSkeleton />
                            </>
                        ) : (
                            paymentSummaryData.map((paymentData) => (
                            <PaymentSummaryCard
                                key={paymentData.id}
                                title={paymentData.title}
                                subtitle={paymentData.subtitle}
                                missedCount={paymentData.missedCount}
                                dueCount={paymentData.dueCount}
                                {...(paymentData.totalOutstanding !== undefined && { totalOutstanding: paymentData.totalOutstanding })}
                                onPress={() => {
                                    trackCatalogEvent({
                                        screen: DT_SCREEN.PAYMENTS,
                                        actionName: 'payments_tab_property_select',
                                        eventType: 'action',
                                        properties: { propertyId: paymentData.id },
                                    });
                                    if (paymentData.id !== 'service-charge-payments') {
                                        router.push('/(auth)/property-payments' as any);
                                        return;
                                    }
                                    const paymentIds = (paymentData as { paymentIds?: string[] }).paymentIds ?? [];
                                    const selectableIds = isAdhaJourney
                                        ? paymentIds
                                        : ((paymentData as { payablePaymentIds?: string[] }).payablePaymentIds ?? []);
                                    openServiceChargePayments({
                                        paymentIds,
                                        selectableIds,
                                        isWeb: Platform.OS === 'web',
                                        openSingleOnWeb: (paymentId) => {
                                            setSelectedPaymentId(paymentId);
                                            setPaymentDetailsVisible(true);
                                        },
                                        openSingleNative: (paymentId) => {
                                            router.push(`/(auth)/payment-details?id=${paymentId}` as any);
                                        },
                                        openSelectedList: (selectedPaymentIds) => {
                                            router.push(`/(auth)/property-payments?tab=upcoming&selectedPaymentIds=${selectedPaymentIds.join(',')}` as any);
                                        },
                                        openEmptyList: () => {
                                            router.push('/(auth)/property-payments' as any);
                                        },
                                    });
                                }}
                            >
                                {/*
                                    TODO: Cheques available section is temporarily disabled.
                                    Re-enable this block when cheques UX / API is finalized.

                                    Original implementation:
                                    {paymentData.cheques.length > 0 && (
                                        <View style={styles.chequesSection}>
                                            <Typography variant="subhead" color="primary" style={styles.chequesTitle}>
                                                {t('payments.chequesAvailable')}
                                            </Typography>
                                            <View style={styles.chequesBleed}>
                                                <ScrollView
                                                    horizontal
                                                    showsHorizontalScrollIndicator={false}
                                                    contentContainerStyle={styles.chequesContainer}
                                                >
                                                    {paymentData.cheques.map((cheque) => (
                                                        <ChequeItem
                                                            key={cheque.id}
                                                            propertyName={cheque.propertyName}
                                                            chequeCount={cheque.chequeCount}
                                                            amount={cheque.amount}
                                                        />
                                                    ))}
                                                </ScrollView>
                                            </View>
                                        </View>
                                    )}
                                */}
                            </PaymentSummaryCard>
                            ))
                        )}
                    </View>
                </View>
            </ScrollView>

            {/* Payment details dialog for web – route is not changed */}
            {Platform.OS === 'web' && (
                <PaymentDetailsDialog
                    visible={paymentDetailsVisible}
                    paymentId={selectedPaymentId}
                    onClose={() => setPaymentDetailsVisible(false)}
                />
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    headerContainer: {
        paddingHorizontal: 0,
    },
    content: {
        paddingHorizontal: 16,
        paddingTop: 16,
        gap: 16,
    },
    scrollContent: {
        paddingBottom: 24,
    },
    paymentCardsContainer: {
        gap: 16,
    },
    chequesSection: {
        gap: 8,
    },
    chequesTitle: {
        marginBottom: 0,
    },
    chequesContainer: {
        flexDirection: 'row',
        gap: 8,
        flexWrap: 'nowrap',
        overflow: 'hidden',
        paddingHorizontal: 16,
    },
    chequesBleed: {
        marginHorizontal: -16,
    },
});
